import * as React from "react";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { RouteStore } from "bernie-core";

import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";

import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { Travelers } from "components/shared/Travelers/Travelers";
import { Dates } from "components/shared/Dates/Dates";
import { TimeDropdown } from "components/shared/TimeDropdown/TimeDropdown";
import { CarWizardState } from "stores/wizard/state/car/CarWizardState";
import { GtConfig } from "stores/wizard/config/staticConfigs/car/typings";

import { locKeys } from "../l10n";
import { GTHiddenSearchData } from "./GTHiddenSearchData";
import { BookRoundtrip } from "./BookRoundtrip";
import { GroundTransportLocation } from "./Location/GroundTransportLocation";
import { BookRoundtripToggle } from "./BookRoundtripToggle";
import { GlobalConfig } from "stores/wizard/config";

export interface GroundTransportationPWAViewProps {
  carWizardState: CarWizardState;
  toggleBookRoundtrip: () => void;
  setInputsRefs?: (ref: React.RefObject<HTMLInputElement>) => void;
  getLocError: (isLink: boolean) => string | undefined;
  gtConfig: GtConfig;
  routeStore: RouteStore;
  globalConfig?: GlobalConfig;
}

export const GroundTransportPWAView = withStores("routeStore")(
  observer((props: GroundTransportationPWAViewProps) => {
    const {
      carWizardState,
      carWizardState: {
        pickupTime,
        isGroundTransportFormValid,
        returnTime,
        roundTrip,
        groundTransportationLocationState,
        updateInvalidFormsState,
      },
      setInputsRefs,
      toggleBookRoundtrip,
      routeStore,
    } = props;
    const { gtArrivalTime } = locKeys;
    const { isOriginAirport, updateFieldsFromQueryParams, airportCode, hotelId } = groundTransportationLocationState;
    const format12hrConfig = carWizardState.config.timeFormats.timeFormat12Hour;

    const mainGridLayout: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 12 };

    const locationColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 12 };

    const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 4 };
    const datesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 4 };
    const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 4 };
    const errorSummaryGridColumns: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };

    const roundTripDatesColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };
    const roundTripTimeColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 3 };

    if (isGroundTransportFormValid) {
      updateInvalidFormsState();
    }
    updateFieldsFromQueryParams(routeStore);

    return (
      <>
        <UitkSpacing margin={{ blockstart: "three" }}>
          <UitkLayoutGrid columns={mainGridLayout} space="three">
            <>
              {!isGroundTransportFormValid && (
                <LobErrorSummary
                  locHeadingToken={props.getLocError(false)!}
                  locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
                  locLinkToken={props.getLocError(true)}
                  linkClickFocusId="id-roundtrip-error"
                  inputErrorRef={carWizardState.errorInputRef}
                  colSpan={errorSummaryGridColumns}
                  setInputsRefs={setInputsRefs}
                />
              )}

              <GroundTransportLocation
                groundTransportationLocationState={groundTransportationLocationState}
                colSpan={locationColSpan}
                setInputsRefs={setInputsRefs}
                lobState={carWizardState}
              />
              <Travelers lobState={carWizardState} colSpan={travelersColSpan} config={props.gtConfig.travelers} />
              <Dates lobState={carWizardState} singleDate={true} colSpan={datesColSpan} setInputsRefs={setInputsRefs} />
              <TimeDropdown
                labelKey={gtArrivalTime}
                timeValues={carWizardState.config.gtConfig.timeValues}
                onChange={carWizardState.setPickupTime}
                value={pickupTime}
                invalidKey={carWizardState.timeStartInvalidKey}
                colSpan={timeDropdownColSpan}
                use12hr={format12hrConfig}
              />
              {isOriginAirport && (
                <BookRoundtripToggle toggleBookRoundtrip={toggleBookRoundtrip} roundTripChecked={roundTrip} />
              )}
              {isOriginAirport && roundTrip && (
                <BookRoundtrip
                  carWizardState={carWizardState}
                  datesColSpan={roundTripDatesColSpan}
                  timeColSpan={roundTripTimeColSpan}
                  setInputsRefs={setInputsRefs}
                />
              )}
            </>
          </UitkLayoutGrid>
        </UitkSpacing>
        <SubmitRow lobState={props.carWizardState} globalConfig={props.globalConfig} />
        <GTHiddenSearchData
          pickUpTime={pickupTime}
          dropOffTime={roundTrip ? returnTime : ""}
          direction={isOriginAirport}
          airportCode={airportCode}
          hotelId={hotelId}
        />
      </>
    );
  })
);
